import type { MessageKey } from "./en";

/** Spanish UI catalog. Every key in {@link en} must be present. */
export const es: Record<MessageKey, string> = {
  "app.title": "Rescript — edita videos como editas texto",
  "common.cancel": "Cancelar",
  "common.close": "Cerrar",
  "common.delete": "Eliminar",
  "common.download": "Descargar",
  "common.import": "Importar",
  "common.loading": "Cargando",
  "common.remove": "Quitar",
  "common.restore": "Restaurar",
  "common.retry": "Intentar de nuevo",
  "common.searchOrCreate": "Buscar o crear…",
  "common.settings": "Ajustes",
  "common.system": "Sistema",
  "common.tools": "Herramientas",
  "settings.appearance": "Apariencia",
  "settings.light": "Claro",
  "settings.dark": "Oscuro",
  "settings.interfaceLanguage": "Idioma de la interfaz",
  "settings.privacy": "Privacidad",
  "settings.helpImprove": "Ayuda a mejorar la app",
  "settings.telemetryHelp": "Envía estadísticas anónimas de uso de funciones e informes de fallos.",
  "settings.support": "Soporte / comentarios",
  "settings.reportIssue": "Informar de un problema",
  "settings.homepage": "Página de inicio",
  "settings.github": "GitHub",
  "settings.followX": "Seguir en X",
  "model.transcriptSource": "Origen de la transcripción",
  "model.language": "Idioma",
  "model.transcriptLanguage": "Idioma de transcripción",
  "model.importTranscript": "Importar transcripción",
  "upload.recentProjects": "Proyectos recientes",
  "upload.removeRecent": "Quitar de recientes",
  "upload.dropPrefix": "Suelta un archivo de video o audio aquí, o",
  "upload.browse": "explora",
  "upload.chooseTranscriptFirst": "Elige una transcripción en el menú de arriba y luego suelta el medio",
  "upload.willUseTranscript": "Se usará {name} · MP4, WebM, MOV, MP3, WAV, …",
  "upload.mediaFormats": "MP4, WebM, MOV, MP3, WAV, M4A, …",
  "upload.gettingReady": "Preparando todo",
  "upload.gettingReadyHelp": "Configurando el motor multimedia; esto solo ocurre una vez.",
  "upload.unsupported": "Este navegador no puede ejecutar el editor",
  "upload.unsupportedHelp":
    "La edición necesita SharedArrayBuffer, que requiere una página aislada entre orígenes. Prueba una versión reciente de Chrome, Edge, Safari o Firefox mediante HTTPS.",
  "upload.unsupportedSimd": "Este dispositivo no puede ejecutar el editor",
  "upload.unsupportedSimdHelp": "La edición necesita WebAssembly SIMD, que este procesador no declara compatible. En un PC con Windows en Arm, actualiza a la última versión de Rescript: los instaladores actuales incluyen una versión Arm64 nativa que no se ejecuta emulada.",
  "upload.transcribeTitle": "Transcribir",
  "upload.transcribeText": "Whisper localmente, o importa SRT / VTT.",
  "upload.editTitle": "Editar",
  "upload.editText": "Selecciona palabras y pulsa Suprimir para editar.",
  "upload.exportTitle": "Exportar",
  "upload.exportText": "Renderiza el corte final a MP4 o M4A.",
  "editor.chooseTranscript": "Primero elige un archivo de transcripción desde el menú de origen.",
  "editor.chooseMedia": "Elige un archivo de video o audio.",
  "editor.undo": "Deshacer (⌘Z)",
  "editor.redo": "Rehacer (⇧⌘Z)",
  "editor.export": "Exportar",
  "topbar.startOver": "Empezar de nuevo",
  "transcript.replace": "Reemplazar transcripción desde SRT, VTT o JSON",
  "transcript.header": "Transcripción",
  "transcript.wordDeleted": "{count} palabra",
  "transcript.wordsDeleted": "{count} palabras",
  "transcript.replaceConfirm": "¿Reemplazar la transcripción actual con este archivo?",
  "transcript.invalidFile": "Elige un archivo de transcripción SRT, VTT o JSON.",
  "transcript.noSpeech": "No se detectó voz. Asegúrate de que este archivo tenga audio, o importa una transcripción.",
  "transcript.cut": "Cortar",
  "transcript.follow": "Seguir cabezal",
  "transcript.hideDeleted": "Ocultar palabras eliminadas",
  "transcript.showDeleted": "Mostrar palabras eliminadas",
  "transcript.correct": "Corregir",
  "transcript.scrollWithPlayhead": "Desplazar con el cabezal",
  "transcript.joinSplit": "Clip dividido — haz clic para unir estos clips",
  "transcript.joinClips": "Unir clips",
  "transcript.hesitation": "Vacilación detectada (no transcrita) — córtala con Quitar muletillas",
  "transcript.textLabel": "Texto de la transcripción",
  "transcript.textHint": "Las flechas recorren las palabras y mueven el cabezal. Mayús con flechas selecciona. Retroceso o Supr cortan palabras, Intro las corrige, Espacio reproduce o pausa, y Deshacer recupera los cortes.",
  "transcript.textHesitation": "[vacilación]",
  "transcript.textCut": "Cortado: {text}",
  "transcript.textNothingToCut": "Nada que cortar",
  "transcript.textUndo": "Deshacer",
  "transcript.textRedo": "Rehacer",
  "tools.bulk": "Limpiezas masivas de transcripción",
  "tools.removeFillers": "Quitar muletillas",
  "tools.removeFillersTitle": "Corta muletillas (\"eh\", \"mmm\", \"...\", …) del video",
  "tools.restoreFillers": "Restaurar muletillas",
  "tools.restoreFillersTitle": "Devuelve cada muletilla cortada",
  "tools.removeSilences": "Quitar silencios",
  "tools.removeSilencesTitle": "Corta pausas y silencios (≥{seconds} s) del video",
  "tools.restoreSilences": "Restaurar silencios",
  "tools.restoreSilencesTitle": "Devuelve cada pausa cortada",
  "timeline.removed": "{trimmed} quitado — duración original {duration}",
  "timeline.back5": "Retroceder 5 s",
  "timeline.playPause": "Reproducir / pausar (espacio)",
  "timeline.forward5": "Avanzar 5 s",
  "timeline.split": "Dividir",
  "timeline.splitTitle": "Dividir clip en el cabezal (S)",
  "timeline.splitDisabled": "Mueve el cabezal a una región conservada para dividir",
  "timeline.delete": "Eliminar",
  "timeline.deleteTitle": "Eliminar clip seleccionado (Suprimir)",
  "timeline.deleteDisabled": "Selecciona un clip en la línea de tiempo para eliminarlo",
  "timeline.restore": "Restaurar",
  "timeline.restoreTitle": "Restaurar corte seleccionado (Suprimir)",
  "timeline.restoreDisabled": "Selecciona un corte o una sección de silencio en la línea de tiempo para restaurar",
  "timeline.zoomOut": "Alejar",
  "timeline.fit": "Ajustar a la ventana",
  "timeline.zoomIn": "Acercar — arrastra los bordes de las palabras para ajustar el tiempo",
  "timeline.joinClips": "Unir estos clips (quitar división)",
  "timeline.trimStart": "Recortar inicio del clip",
  "timeline.trimEnd": "Recortar final del clip",
  "timeline.hesitationAdjust": "… vacilación detectada — arrastra los bordes para ajustar, o córtala con Quitar muletillas",
  "timeline.hesitationCut": "… vacilación detectada — córtala con Quitar muletillas",
  "timeline.dragTiming": "{word} — arrastra los bordes para ajustar el tiempo",
  "timeline.scrollZoom": "Desplázate para acercar / alejar",
  "speaker.moveStart": "Arrastra para mover dónde empieza este hablante",
  "speaker.moveLabel": "Mover etiqueta del hablante",
  "speaker.options": "Opciones del hablante",
  "speaker.change": "Cambiar hablante",
  "speaker.rename": "Renombrar hablante",
  "speaker.renameAction": "Renombrar",
  "speaker.replace": "Reemplazar en el proyecto por…",
  "speaker.removeProject": "Quitar del proyecto",
  "speaker.newName": "Nuevo nombre",
  "speaker.find": "Buscar un hablante…",
  "speaker.noMatches": "No hay hablantes coincidentes",
  "speaker.renameTo": "Renombrar {current} a {next}",
  "speaker.button": "Hablante",
  "speaker.search": "Buscar o crear…",
  "speaker.defaultName": "Hablante {number}",
  "speaker.create": "Crear \"{name}\"",
  "export.title": "Exportar",
  "export.type": "Tipo de exportación",
  "export.video": "Video",
  "export.audio": "Audio",
  "export.transcript": "Transcripción",
  "export.subtitles": "Subtítulos",
  "export.videoUnavailable": "La exportación de video no está disponible para proyectos solo de audio",
  "export.noAudio": "Este archivo no tiene pista de audio",
  "export.noWordsFirst": "Transcribe o importa una transcripción primero",
  "export.format": "Formato",
  "export.resolution": "Resolución",
  "export.original": "Original",
  "export.plainText": "TXT",
  "export.markdown": "Markdown",
  "export.statOriginal": "Original",
  "export.statCuts": "Cortes",
  "export.statEdited": "Editado",
  "export.includeTimestamps": "Incluir marcas de tiempo",
  "export.speakerLabels": "Incluir nombres de hablantes",
  "export.shortCues": "Subtítulos cortos (≤5 s)",
  "export.transcriptHelp": "Texto con etiquetas de hablante y cortes eliminados.",
  "export.transcriptHelpTimestamps":
    "Texto con etiquetas de hablante y cortes eliminados. Cada turno empieza con su tiempo en la línea de tiempo editada.",
  "export.transcriptHelpPdf":
    "PDF usa una fuente latina estándar — preferir DOCX para Unicode completo (CJK, etc.).",
  "export.subtitlesHelp":
    "SRT y VTT usan la línea de tiempo editada (con cortes aplicados). JSON conserva la lista completa de palabras para reimportar.",
  "export.subtitlesHelpShortCues":
    "SRT y VTT usan la línea de tiempo editada (con cortes aplicados), divididos en subtítulos cortos por frase (≤5 s). JSON conserva la lista completa de palabras para reimportar.",
  "export.encodingHelp": "Recodificando con ffmpeg.wasm — los archivos largos tardan un poco.",
  "export.reexport": "Reexportar con las últimas ediciones",
  "export.rendering": "Renderizando en tu navegador…",
  "export.downloadFile": "Descargar {name}",
  "export.saveFile": "Guardar {name}…",
  "export.saving": "Guardando…",
  "export.saveDialogTitle": "Guardar exportación",
  "export.savedAs": "Guardado como {name}",
  "export.showInFinder": "Mostrar en Finder",
  "export.showInExplorer": "Mostrar en el Explorador",
  "export.showInFolder": "Mostrar en la carpeta",
  "export.renderingNative": "Renderizando…",
  "export.encodingHelpNative": "Recodificando con ffmpeg — los archivos largos tardan un poco.",
  "export.downloadFormat": "Descargar .{format}",
  "export.exportFormat": "Exportar {format}",
  "export.timeline": "Línea de tiempo",
  "export.timelineTitle": "Exportar una secuencia NLE/DAW (Resolve, Premiere, Final Cut, Pro Tools, Reaper, Blender)",
  "export.nle": "Formato",
  "export.frameRate": "Velocidad de fotogramas",
  "export.timelineHelp":
    "La secuencia referencia el medio original por nombre de archivo — vuelve a vincularlo en el NLE después de importar.",
  "export.timelineHelpAaf": "AAF de Pro Tools / Logic (solo metadatos).",
  "export.timelineHelpFcpx": "FCPXML de Final Cut Pro.",
  "export.timelineHelpPremiere": "XML de Adobe Premiere Pro (xmeml).",
  "export.timelineHelpResolve": "XML de DaVinci Resolve (xmeml).",
  "export.timelineHelpReaper": "Proyecto de Reaper (.rpp).",
  "export.timelineHelpSamplitude":
    "EDL de Samplitude (.edl) — también se abre directamente en Reaper.",
  "export.timelineHelpBlender":
    "Proyecto de edición de vídeo de Blender (.blend, Blender 5.2+) — guárdalo en la misma carpeta que el archivo original.",
  "export.aafOverCap":
    "Esta edición tiene {count} clips; AAF admite hasta {max}. Usa Resolve, Premiere, Final Cut, Reaper o EDL de Samplitude.",
  "export.preparing": "Preparando…",
  "import.reading": "Leyendo transcripción…",
  "import.importing": "Importando…",
  "import.failed": "Error al importar",
  "import.readingFile": "Leyendo archivo…",
  "import.chooseFileShort": "Elige un archivo…",
  "import.chooseFile": "Elige una transcripción SRT, VTT o JSON",
  "import.selected": "{name} seleccionado",
  "banner.faster": "¿Quieres transcripciones y exportaciones más rápidas?",
  "banner.getDesktop": "Obtén la app de escritorio de Rescript",
  "banner.downloadFor": "Descargar para {platform}",
  "banner.dismiss": "Descartar",
  "social.githubRepo": "Repositorio de GitHub",
  "social.discordServer": "Servidor de Discord",
  "social.xProfile": "Perfil de X",
  "globalError.title": "Rescript — algo salió mal",
  "globalError.heading": "Algo salió mal",
  "globalError.body":
    "El editor encontró un error inesperado. Tus proyectos guardados siguen en este dispositivo; al recargar deberían volver.",
  "progress.loadingMedia": "Cargando medios…",
  "progress.loadingMediaEngine": "Cargando motor multimedia…",
  "progress.extractingAudio": "Extrayendo audio…",
  "progress.preparingMedia": "Preparando el medio…",
  "progress.loadingSpeechModel": "Cargando modelo de voz…",
  "progress.loadingSpeechCache": "Cargando modelo de voz desde la caché…",
  "progress.downloadingSpeech": "Descargando modelo de voz…",
  "progress.gpuFallback": "La GPU se interrumpió — continuando en CPU…",
  "progress.detectingSpeech": "Detectando voz…",
  "progress.transcribing": "Transcribiendo…",
  "progress.loadingAlignCache": "Cargando modelo de alineación desde la caché…",
  "progress.downloadingAlign": "Descargando modelo de alineación…",
  "progress.aligning": "Alineando palabras…",
  "progress.speakers": "Identificando hablantes…",
  "error.selectModel": "Selecciona un modelo de voz para transcribir.",
  "error.workerCrashed": "El worker de transcripción falló.",
  "error.mediaEngineNetwork": "No se pudo cargar el motor multimedia — se cortó la conexión. Revisa tu internet e inténtalo de nuevo.",
  "error.mediaEngineStalled": "El motor multimedia dejó de responder y se reinició. Cierra otras apps y pestañas para liberar memoria e inténtalo de nuevo.",
  "error.mediaEngineNotReady": "El motor multimedia aún no está listo — recarga la página e inténtalo de nuevo.",
  "error.simdUnsupported": "Este procesador no admite WebAssembly SIMD, que el motor multimedia necesita. En un PC con Windows en Arm, actualiza a la última versión de Rescript: los instaladores actuales incluyen una versión Arm64 nativa que no se ejecuta emulada.",
  "error.processFile": "No se pudo procesar este archivo.",
  "error.extractAudio": "No se pudo extraer audio de este archivo.",
  "error.nothingToExport": "Todo se ha eliminado — no hay nada que exportar.",
  "error.videoExport": "La exportación falló al renderizar el video.",
  "error.audioExport": "La exportación falló al renderizar el audio.",
  "error.exportSave": "No se pudo guardar el archivo exportado.",
  "error.diskFull": "No hay suficiente espacio en disco para terminar. Libera espacio e inténtalo de nuevo.",
  "error.export": "Error al exportar.",
  "error.timelineExport": "Error al exportar la línea de tiempo.",
  "error.timelineEmpty": "No hay nada que poner en la línea de tiempo.",
  "error.aafTemplate": "No se pudo cargar la plantilla de exportación AAF.",
  "error.emptyTranscript": "Ese archivo de transcripción está vacío.",
  "error.noTimedWords": "No se encontraron palabras con tiempo en esa transcripción.",
  "error.parseJson": "No se pudo analizar esa transcripción JSON.",
  "error.jsonShape": 'JSON debe ser un array de palabras o { "words": [...] }.',
  "error.noWords": "No hay palabras para exportar.",
  "error.projectMissing": "Ese proyecto ya no está guardado.",
  "error.openProject": "No se pudo abrir ese proyecto.",
  "error.removeProject": "No se pudo quitar ese proyecto.",
  "error.readTranscript": "No se pudo leer esa transcripción.",
  "error.clearRecent": "No se pudieron borrar los proyectos recientes.",
  "error.modelDownload":
    "No se pudo terminar de descargar el modelo de voz — se cortó la conexión. Revisa tu internet e inténtalo de nuevo; las partes ya descargadas se conservan.",
  "error.gpuReset": "La transcripción se interrumpió cuando se reinició la GPU (a menudo después de bloquear la pantalla). Inténtalo de nuevo.",
  "confirm.clearRecent": "¿Quitar todos los proyectos recientes? Sus ediciones guardadas se eliminarán.",
};
